import React, { useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Eye,
  EyeOff,
  Layers,
  LockKeyhole,
  QrCode,
  UserRound
} from 'lucide-react';
import { authService } from '../services/auth';
import './Login.css';

const QR_SIZE = 25;
const isFinderArea = (row, column) => (
  (row < 7 && column < 7)
  || (row < 7 && column >= QR_SIZE - 7)
  || (row >= QR_SIZE - 7 && column < 7)
);
const qrCells = Array.from({ length: QR_SIZE * QR_SIZE }, (_, index) => {
  const row = Math.floor(index / QR_SIZE);
  const column = index % QR_SIZE;
  if (isFinderArea(row, column)) return null;
  const filled = ((row * 11 + column * 7 + row * column) % 13 < 5)
    || (row === 8 && column % 2 === 0)
    || (column === 8 && row % 2 === 0);
  return filled ? { row, column } : null;
}).filter(Boolean);

const offers = [
  {
    icon: QrCode,
    title: 'QR Codes personalizados',
    shortTitle: 'QR Codes',
    description: 'Criação, destino editável e identidade visual.'
  },
  {
    icon: Layers,
    title: 'Páginas publicáveis',
    shortTitle: 'Páginas',
    description: 'Editor de páginas, coleções e questionários.'
  },
  {
    icon: BarChart3,
    title: 'Relatórios de acesso',
    shortTitle: 'Relatórios',
    description: 'Desempenho, cliques e dados de localização.'
  }
];

function QrIllustration() {
  const finders = [[0, 0], [QR_SIZE - 7, 0], [0, QR_SIZE - 7]];
  return (
    <svg className="login-qr" viewBox={`0 0 ${QR_SIZE} ${QR_SIZE}`} role="img" aria-label="Representação de um QR Code">
      {qrCells.map(({ row, column }) => <rect key={`${row}-${column}`} x={column} y={row} width="1" height="1" />)}
      {finders.map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x} y={y} width="7" height="7" />
          <rect className="login-qr-cutout" x={x + 1} y={y + 1} width="5" height="5" />
          <rect x={x + 2} y={y + 2} width="3" height="3" />
        </g>
      ))}
    </svg>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.signIn(username, password);
      onLogin();
    } catch (err) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-entry">
        <header className="login-brand">
          <span className="login-brand-mark"><QrCode size={20} strokeWidth={2.1} /></span>
          <span className="login-brand-name font-display">QRBlant</span>
        </header>

        <div className="login-form-area">
          <div className="login-heading">
            <span>ACESSO INTERNO</span>
            <h1>Entre no sistema</h1>
            <p>Use seu usuário e senha corporativos.</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="username">Usuário</label>
              <div className="login-input-wrap">
                <UserRound size={17} aria-hidden="true" />
                <input
                  type="text"
                  id="username"
                  placeholder="Digite seu usuário"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                  disabled={loading}
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password">Senha</label>
              <div className="login-input-wrap">
                <LockKeyhole size={17} aria-hidden="true" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(value => !value)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={showPassword}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {error && <div className="login-error" id="login-error" role="alert"><span>{error}</span></div>}

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? <><span className="login-spinner" /> Entrando...</> : <>Entrar <ArrowRight size={17} /></>}
            </button>
          </form>

          <div className="login-mobile-scope" aria-label="Recursos do sistema">
            {offers.map(({ icon: Icon, shortTitle }) => <span key={shortTitle}><Icon size={14} />{shortTitle}</span>)}
          </div>
        </div>

        <footer className="login-entry-footer">
          <span>Blant Cosméticos</span>
          <span>Uso interno</span>
        </footer>
      </section>

      <aside className="login-product" aria-label="Recursos do QRBlant">
        <div className="login-product-header">
          <span>QRBLANT / PLATAFORMA INTERNA</span>
          <h2>Recursos do sistema</h2>
          <p>Ferramentas para criar, publicar e analisar os projetos digitais da empresa.</p>
        </div>

        <div className="login-product-body">
          <figure className="login-qr-stage">
            <div className="login-qr-paper"><QrIllustration /></div>
            <figcaption><span>QR CODE DINÂMICO</span><small>destino e visual editáveis</small></figcaption>
          </figure>

          <div className="login-offers">
            {offers.map(({ icon: Icon, title, description }, index) => (
              <article className="login-offer" key={title}>
                <span className="login-offer-number">0{index + 1}</span>
                <Icon size={19} strokeWidth={1.8} />
                <div><strong>{title}</strong><p>{description}</p></div>
              </article>
            ))}
          </div>
        </div>

        <div className="login-product-footer"><span>CRIAR</span><i /><span>PUBLICAR</span><i /><span>ACOMPANHAR</span></div>
      </aside>
    </main>
  );
}

export default Login;
